/**
 * Carry the owner's Edit / View as choice across storefront pages and reloads.
 *
 * The mode lives in `useState` (survives in-app nav) and a cookie + sessionStorage
 * (survives reload). The cookie is what lets SSR render owner tools / Support /
 * page lessons on the first HTML when Edit is already on.
 *
 * Anonymous / never-armed visits still SSR as the public storefront.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const { editorArmed } = useStoreEditorArmed()
  const livePreview = useStoreLivePreview()
  const route = useRoute()

  function remember() {
    writeStoreOwnerMode(editorArmed.value ? (livePreview.value ? 'view' : 'edit') : null)
  }

  // Not `immediate` — the stored mode has to survive until `apply()` reads it.
  watch([editorArmed, livePreview], remember)

  // Client navigations into the studio (creator hub → `?edit=1`, in-app
  // `?studio=app`) happen after this plugin's first apply. The armed flag is
  // useState, so it would otherwise stay on the View as left from last time.
  // `?edit=1` already matches the SSR editor flag, so applying it immediately
  // does not change the hydrated markup.
  watch(
    () => route.fullPath,
    () => {
      enterStudioEditMode(route.query as Record<string, unknown>)
    },
    { immediate: true },
  )

  function apply() {
    nuxtApp.runWithContext(() => {
      // Lift legacy sessionStorage-only tabs into the cookie so the next SSR
      // paint already includes owner chrome.
      const stored = readStoreOwnerMode()
      if (stored) writeStoreOwnerMode(stored)
      restoreStoreOwnerMode()
      // Covers `?edit=1`, where nothing changed for the watcher to pick up.
      remember()
    })
  }

  // After hydration only: arming a *remembered* mode any earlier would make the
  // first client paint disagree with the visitor markup it is hydrating.
  // Use `app:suspense:resolve` only — `app:mounted` fires before async page /
  // layout Suspense finishes hydrating, which flipped `editorArmed` mid-hydrate
  // (is-editable / owner FABs / edit badges mismatching the SSR HTML).
  if (nuxtApp.isHydrating) {
    nuxtApp.hooks.hookOnce('app:suspense:resolve', () => {
      apply()
    })
  }
  else apply()
})
